import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAccessToken } from "../hooks/useAuthentication";
import { useAccess } from "../hooks/useAccess";
import { useTheme } from "../hooks/useTheme";
import { useSplitView, useWideEnoughForSplit } from "../lib/splitView";
import { NAV, landingPath, sectionForPath } from "../nav";
import type { NavItem, NavSection } from "../nav";
import { Candlestick } from "./StockIcons";

// The app's top bar. Five sections (Trading Desk, Fund Facts, Accounts,
// Power BI, Admin) come from the nav manifest in src/nav.ts; a section with
// pages opens a DROPDOWN of grouped links on click (or hover), a section
// without one is a plain link. What is drawn is filtered by the access hook:
// in enforce mode a page the person's groups do not cover is not rendered at
// all and a section with no visible pages leaves the bar; in shadow mode it is
// drawn with a lock so the matrix can be checked before it is switched on.
// Checkpoint keeps its older allow-list and only appears when that passes.

// The theme button shows the CURRENT preference: Monitor (follow the
// browser), Sun (light), Moon (dark); a click cycles to the next one. Inherit
// `currentColor` so they pick up the header text.
const SunIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);
const MoonIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);
const MonitorIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);
const THEME_ICON = { system: MonitorIcon, light: SunIcon, dark: MoonIcon } as const;
const THEME_LABEL = { system: "System", light: "Light", dark: "Dark" } as const;
// Two panes side by side — the split-view toggle and the per-item
// "open over there" affordance share it.
const SplitIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M12 4v16" />
  </svg>
);
const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg viewBox="0 0 24 24" className={`ml-1 inline h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);
export const LockIcon: React.FC<{ className?: string; title?: string }> = ({ className, title }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label={title}>
    {title && <title>{title}</title>}
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

interface HeaderProps {
  hasCheckpointAccess?: boolean | null;
  checkpointLoading?: boolean;
}

const LOCK_HINT = "Not in your groups — hidden once access enforcement is on";

const topClass = (active: boolean) =>
  `relative flex items-center px-2.5 py-4 font-montserrat text-[11px] font-medium tracking-[1px] uppercase whitespace-nowrap transition-all duration-200 ${
    active ? "text-white" : "text-qw-gray hover:text-qw-silver"
  }`;

const ActiveUnderline: React.FC<{ isActive: boolean }> = ({ isActive }) =>
  isActive ? (
    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-qw-teal rounded-full" />
  ) : null;

/** The manifest, narrowed to what THIS person should see (or see locked). */
export const useVisibleNav = (hasCheckpointAccess?: boolean | null): NavSection[] => {
  const { visible } = useAccess();
  return NAV.map((s) => {
    const groups = s.groups
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => (i.requiresCheckpoint ? !!hasCheckpointAccess : visible(i.feature))),
      }))
      .filter((g) => g.items.length > 0);
    return { ...s, groups };
  }).filter((s) => (s.groups.length > 0 ? true : NAV.find((n) => n.key === s.key)!.groups.length === 0 && visible(s.feature)));
};

const Header: React.FC<HeaderProps> = ({ hasCheckpointAccess, checkpointLoading }) => {
  const { instance, accounts } = useAccessToken();
  const { can, locked, mode } = useAccess();
  const { pref: themePref, isDark, next: nextTheme, cycle: cycleTheme } = useTheme();
  const ThemeIcon = THEME_ICON[themePref];
  const themeTitle = `Theme: ${THEME_LABEL[themePref]}${
    themePref === "system" ? ` (${isDark ? "dark" : "light"})` : ""
  } — click for ${THEME_LABEL[nextTheme]}`;
  const { open: splitOpen, path: splitPath, openPane, closePane } = useSplitView();
  const splitAvailable = useWideEnoughForSplit();
  const navigate = useNavigate();
  const location = useLocation();
  const sections = useVisibleNav(hasCheckpointAccess);

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);          // mobile hamburger
  const [mobileOpenKey, setMobileOpenKey] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<number | undefined>(undefined);

  // Close everything when the route changes (a link in the dropdown was used).
  useEffect(() => { setOpenKey(null); setMenuOpen(false); }, [location.pathname]);

  // Click outside / Escape closes the dropdown.
  useEffect(() => {
    if (!openKey) return;
    const onDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenKey(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenKey(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openKey]);

  const hoverOpen = useCallback((key: string) => {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setOpenKey(key), 120);
  }, []);
  const hoverClose = useCallback(() => {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setOpenKey(null), 200);
  }, []);
  const keepOpen = useCallback(() => window.clearTimeout(hoverTimer.current), []);

  const activeSection = sectionForPath(location.pathname)?.key;
  const home = landingPath(can);

  const renderItem = (item: NavItem) => {
    const isLocked = !item.requiresCheckpoint && locked(item.feature);
    return (
      // The row is a group so the split affordance can stay hidden until the
      // pointer is on the item — it is a power-user shortcut, not a second
      // primary action competing with the link itself.
      <div key={item.key} className="group/item relative">
      <NavLink
        to={item.to}
        onClick={() => setOpenKey(null)}
        title={isLocked ? LOCK_HINT : undefined}
        className={({ isActive }) =>
          `group flex items-start gap-2 rounded py-1.5 pl-2.5 pr-8 text-left transition-colors ${
            isActive ? "bg-qw-teal/15 text-content" : "text-content-muted hover:bg-surface-sunken hover:text-content"
          } ${isLocked ? "opacity-60" : ""}`
        }
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-medium leading-tight">
            {item.requiresCheckpoint && checkpointLoading ? "…" : item.label}
          </span>
          {item.hint && <span className="block text-[11px] leading-tight text-content-subtle">{item.hint}</span>}
        </span>
        {isLocked && <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-content-subtle" title={LOCK_HINT} />}
      </NavLink>
      {splitAvailable && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPane(item.to); setOpenKey(null); }}
          title={`Open ${item.label} in the right pane`}
          aria-label={`Open ${item.label} in the right pane`}
          className="absolute right-1 top-1/2 hidden -translate-y-1/2 rounded p-1 text-content-subtle transition-colors hover:bg-surface hover:text-qw-teal focus:block focus:outline-none group-hover/item:block"
        >
          <SplitIcon className="h-3.5 w-3.5" />
        </button>
      )}
      </div>
    );
  };

  return (
    // Header stays dark in both themes (brand bar). The dark-mode hairline
    // separates it from the dark body, which is otherwise a similar tone.
    <header className="relative z-[60] bg-qw-dark print:hidden dark:border-b dark:border-white/10">
      <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4 px-4">
        {/* Wordmark — tagline hidden below 2xl so the nav has more room */}
        <div
          className="flex items-center gap-2 cursor-pointer select-none shrink-0"
          onClick={() => navigate(home)}
        >
          <Candlestick className="w-7 h-7 text-qw-teal" bullish={true} />
          <div className="flex flex-col leading-tight py-3">
            <span className="font-montserrat font-extrabold text-white text-base tracking-[2px] uppercase">
              Q Wealth Partners
            </span>
            <span className="hidden 2xl:inline font-montserrat font-normal text-qw-silver text-[9px] tracking-[4px] uppercase">
              Where Money Meets Meaning
            </span>
          </div>
        </div>

        {/* Desktop navigation: sections with dropdowns. Hidden below lg (the
            hamburger takes over there). */}
        <div ref={navRef} className="hidden lg:flex items-center min-w-0">
          <nav className="flex items-center" aria-label="Primary">
            {sections.map((s) => {
              const active = activeSection === s.key;
              if (s.groups.length === 0) {
                return (
                  <NavLink key={s.key} to={s.to} className={() => topClass(active)}>
                    {s.label}
                    <ActiveUnderline isActive={active} />
                  </NavLink>
                );
              }
              const open = openKey === s.key;
              return (
                <div
                  key={s.key}
                  className="relative"
                  onMouseEnter={() => hoverOpen(s.key)}
                  onMouseLeave={hoverClose}
                >
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={() => setOpenKey(open ? null : s.key)}
                    className={topClass(active || open)}
                  >
                    {s.label}
                    <Chevron open={open} />
                    <ActiveUnderline isActive={active} />
                  </button>
                  {open && (
                    <div
                      role="menu"
                      onMouseEnter={keepOpen}
                      onMouseLeave={hoverClose}
                      className="absolute left-0 top-full z-[70] mt-0 min-w-[280px] rounded-b-lg border border-line-strong bg-surface-overlay p-3 shadow-xl"
                      style={{ minWidth: `${Math.min(s.groups.length, 3) * 220 + 24}px` }}
                    >
                      <div className={`grid gap-4 ${s.groups.length >= 3 ? "grid-cols-3" : s.groups.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
                        {s.groups.map((g) => (
                          <div key={g.title} className="min-w-0">
                            <div className="mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[1.5px] text-content-subtle">
                              {g.title}
                            </div>
                            <div className="flex flex-col">{g.items.map(renderItem)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {mode === "shadow" && (
            <span
              className="ml-1 hidden xl:inline font-montserrat text-[9px] uppercase tracking-[1px] text-qw-gray"
              title="Access checks are running in preview: nothing is hidden yet, locked items show what will be."
            >
              access preview
            </span>
          )}

          {/* Split view: a second pane beside this one, showing any other page. */}
          {splitAvailable && (
            <button
              onClick={() => (splitOpen ? closePane() : openPane(splitPath || home))}
              aria-label={splitOpen ? "Close the split view" : "Open a second pane"}
              aria-pressed={splitOpen}
              title={splitOpen ? "Close the split view" : "Open a second pane beside this one"}
              className={`ml-1 flex h-8 w-8 items-center justify-center rounded transition-colors ${
                splitOpen ? "bg-white/10 text-qw-teal" : "text-qw-gray hover:text-white"
              }`}
            >
              <SplitIcon className="h-[17px] w-[17px]" />
            </button>
          )}

          {/* Theme toggle */}
          <button
            onClick={cycleTheme}
            aria-label={themeTitle}
            title={themeTitle}
            className="ml-1 flex h-8 w-8 items-center justify-center rounded text-qw-gray hover:text-white transition-colors"
          >
            <ThemeIcon className="h-[18px] w-[18px]" />
          </button>

          {/* Divider */}
          <div className="w-px h-5 bg-qw-charcoal mx-2" />

          {/* Auth */}
          {accounts?.length > 0 ? (
            <button
              onClick={() => instance?.logoutRedirect()}
              className="font-montserrat text-xs font-medium tracking-[1px] text-qw-coral hover:text-white transition-all px-3 py-2"
            >
              Log Out
            </button>
          ) : (
            <button
              onClick={() => instance?.loginRedirect()}
              className="font-montserrat text-xs font-medium tracking-[1px] text-qw-teal hover:text-white transition-all px-3 py-2"
            >
              Log In
            </button>
          )}
        </div>

        {/* Hamburger — phones / narrow PWA windows only */}
        <button
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          className="lg:hidden flex h-10 w-10 shrink-0 items-center justify-center rounded text-qw-silver hover:text-white transition-colors"
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {menuOpen ? (
              <>
                <line x1="5" y1="5" x2="19" y2="19" />
                <line x1="19" y1="5" x2="5" y2="19" />
              </>
            ) : (
              <>
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu: sections as an accordion */}
      {menuOpen && (
        <nav className="lg:hidden border-t border-qw-charcoal px-4 pb-3" aria-label="Primary">
          {sections.map((s) => {
            const active = activeSection === s.key;
            if (s.groups.length === 0) {
              return (
                <NavLink
                  key={s.key}
                  to={s.to}
                  onClick={() => setMenuOpen(false)}
                  className={`block w-full text-left px-2 py-3 font-montserrat text-xs font-medium tracking-[1px] uppercase transition-colors ${
                    active ? "text-white" : "text-qw-gray hover:text-qw-silver"
                  }`}
                >
                  {active && <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-qw-teal align-middle" />}
                  {s.label}
                </NavLink>
              );
            }
            const open = mobileOpenKey === s.key || (mobileOpenKey === null && active);
            return (
              <div key={s.key}>
                <button
                  type="button"
                  onClick={() => setMobileOpenKey(open ? "" : s.key)}
                  aria-expanded={open}
                  className={`flex w-full items-center justify-between px-2 py-3 font-montserrat text-xs font-medium tracking-[1px] uppercase transition-colors ${
                    active ? "text-white" : "text-qw-gray hover:text-qw-silver"
                  }`}
                >
                  <span>
                    {active && <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-qw-teal align-middle" />}
                    {s.label}
                  </span>
                  <Chevron open={open} />
                </button>
                {open && (
                  <div className="mb-2 ml-3 border-l border-qw-charcoal pl-3">
                    {s.groups.map((g) => (
                      <div key={g.title} className="mb-2">
                        <div className="px-2 pt-1 text-[9px] font-semibold uppercase tracking-[1.5px] text-qw-gray">{g.title}</div>
                        {g.items.map((item) => (
                          <NavLink
                            key={item.key}
                            to={item.to}
                            onClick={() => setMenuOpen(false)}
                            className={({ isActive }) =>
                              `flex items-center gap-2 px-2 py-2 font-montserrat text-xs transition-colors ${
                                isActive ? "text-white" : "text-qw-silver hover:text-white"
                              }`
                            }
                          >
                            {item.label}
                            {!item.requiresCheckpoint && locked(item.feature) && (
                              <LockIcon className="h-3 w-3 text-qw-gray" title={LOCK_HINT} />
                            )}
                          </NavLink>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div className="my-2 h-px bg-qw-charcoal" />
          <button
            onClick={cycleTheme}
            aria-label={themeTitle}
            className="flex w-full items-center gap-2 px-2 py-3 font-montserrat text-xs font-medium tracking-[1px] uppercase text-qw-gray hover:text-qw-silver transition-colors"
          >
            <ThemeIcon className="h-4 w-4" />
            Theme: {THEME_LABEL[themePref]}
          </button>
          {accounts?.length > 0 ? (
            <button
              onClick={() => { setMenuOpen(false); instance?.logoutRedirect(); }}
              className="block w-full text-left px-2 py-3 font-montserrat text-xs font-medium tracking-[1px] uppercase text-qw-coral hover:text-white transition-colors"
            >
              Log Out
            </button>
          ) : (
            <button
              onClick={() => { setMenuOpen(false); instance?.loginRedirect(); }}
              className="block w-full text-left px-2 py-3 font-montserrat text-xs font-medium tracking-[1px] uppercase text-qw-teal hover:text-white transition-colors"
            >
              Log In
            </button>
          )}
        </nav>
      )}
    </header>
  );
};

export default Header;
